<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import type { Services } from "@services"
import { createUniqueId } from "@core/helpers"
import { usePvStore } from "@modules/pv/stores/pv.store"
import type { ExtrapolationHorairePvAnalysis } from "@modules/pv/types/extrapolation-pv.types"
import {
    buildPvConsommationPlotlyGraph,
    extractPvConsommationYAxisLabel,
    getBarplotHasSecondaryTone,
} from "@modules/pv/utils/buildPvConsommationPlotlyGraph"
import { useChartExport } from "@modules/pv/composables/useChartExport"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Bloc graphique `energie_elec_tout` (variables source PV). */
    consoData: unknown
    /** Bloc graphique `extrapolation_pv_daily` lorsqu’une extrapolation existe. */
    extrapolationData: unknown | null
    /** Nombre de jours avec données horaires (seuil extrapolation : 30). */
    nbHourlyDays: number
    /** Identifiant analyse extrapolation, si calculée. */
    idExtrapolation: string | null
    buildingId: string
    sourceId: string
}>()

const emit = defineEmits<{
    /** Rechargement des données source après action métier. */
    refreshed: []
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Consommation en électricité",
            missingHourlyData:
                "Nous avons besoin d’au moins 30 jours de données pour pouvoir extrapoler les données horaires. Pour ce compteur, nous n’avons {daysPhrase} de données horaires. Nous ne pouvons donc pas faire l’extrapolation.",
            daysNone: "aucun jour",
            daysCount: "que {n} jours",
            exportData: "Export des données",
            exportExcel: "Télécharger Excel",
            extrapolationShow: "Afficher l’extrapolation",
            extrapolationHide: "Masquer l’extrapolation",
            extrapolationTooltipDelete: "Supprimer l’extrapolation",
            confirmDeleteTitle: "Supprimer l’extrapolation",
            confirmDeleteMessage:
                "Supprimer définitivement l’analyse d’extrapolation horaire ? Les graphiques repasseront sur les seules données Enedis.",
            confirmAccept: "Supprimer",
            confirmReject: "Annuler",
            extrapolate: "Extrapoler les données",
            legendHourlyOk: "Données horaires en soutirage disponibles",
            legendHourlyMissing: "Données horaires en soutirage indisponibles",
            legendEnedis: "Consommation - Données Enedis",
            legendExtrapolated: "Consommation - Données extrapolée",
            toastExtrapolationStarted:
                "Extrapolation lancée. Les graphiques se mettront à jour une fois le calcul terminé.",
            toastExtrapolationError:
                "Impossible de lancer l’extrapolation. Réessayez plus tard.",
            toastDeleteOk: "L’analyse d’extrapolation a été supprimée.",
            toastDeleteError:
                "La suppression de l’analyse d’extrapolation a échoué.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")
if (!services) {
    throw new Error("PvConsommationElectriciteCard: services non injectés")
}

const { $createAnalyseExtrapolation, $deleteAnalysePvExtrapolationById } =
    services

const pvStore = usePvStore()
const { extrapolation: extrapolationDoc } = storeToRefs(pvStore)
const toast = useToast()
const confirm = useConfirm()
const { downloadChartAsExcel } = useChartExport()

// ================================
// STATE
// ================================
const showExtrapolationView = ref(!!props.idExtrapolation)
const exportDialogVisible = ref(false)
const isExtrapolating = ref(false)
const isDeleting = ref(false)
const chartInstanceKey = ref(createUniqueId())

// ================================
// COMPUTED
// ================================
const getDaysPhrase = computed(() => {
    if (props.nbHourlyDays === 0) return t("daysNone")
    return t("daysCount", { n: props.nbHourlyDays })
})

const getMissingHourlyMessage = computed(() =>
    t("missingHourlyData", { daysPhrase: getDaysPhrase.value })
)

/**
 * Bloc graphique extrapolation : d’abord `variables.extrapolation_pv_daily`
 * (GET source/pv), sinon `charts.extrapolation_pv_daily` du GET analyse (store).
 */
const getExtrapolationChartBlock = computed(() => {
    if (props.extrapolationData) return props.extrapolationData
    const doc = extrapolationDoc.value as ExtrapolationHorairePvAnalysis | null
    const charts = doc?.charts as Record<string, unknown> | undefined
    const block = charts?.extrapolation_pv_daily
    if (block && typeof block === "object") return block
    return null
})

const getActiveChartPayload = computed(() => {
    if (showExtrapolationView.value && getExtrapolationChartBlock.value) {
        return getExtrapolationChartBlock.value
    }
    return props.consoData
})

const getShowSecondaryExtrapolation = computed(
    () =>
        (getBarplotHasSecondaryTone(getActiveChartPayload.value) &&
            props.nbHourlyDays > 30) ||
        !!props.idExtrapolation ||
        !!getExtrapolationChartBlock.value
)

/** Axe Y identique vue Enedis / extrapolation (kWh), le bloc extrapolation peut avoir un autre `y_label`. */
const getStableYAxisLabel = computed(() => {
    const fromConso = extractPvConsommationYAxisLabel(props.consoData).trim()
    return fromConso.length > 0 ? fromConso : "kWh"
})

const getPlotlyGraph = computed(() =>
    buildPvConsommationPlotlyGraph(getActiveChartPayload.value, {
        chartTitle: t("title"),
        legendPrimary: showExtrapolationView.value
            ? t("legendEnedis")
            : t("legendHourlyOk"),
        legendSecondary: showExtrapolationView.value
            ? t("legendExtrapolated")
            : t("legendHourlyMissing"),
        yAxisLabel: getStableYAxisLabel.value,
    })
)

const getExtrapolationSecondaryLabel = computed(() => {
    if (props.idExtrapolation) {
        return showExtrapolationView.value
            ? t("extrapolationHide")
            : t("extrapolationShow")
    }
    return t("extrapolate")
})

const getExtrapolationDocForExport = computed(
    () => extrapolationDoc.value as unknown as Record<string, unknown> | null
)

// ================================
// WATCHERS
// ================================
watch(
    () => props.idExtrapolation,
    (id) => {
        showExtrapolationView.value = !!id
    }
)

watch(
    () => [
        showExtrapolationView.value,
        getExtrapolationChartBlock.value,
        props.consoData,
    ],
    () => {
        chartInstanceKey.value = createUniqueId()
    },
    { deep: true }
)

watch(exportDialogVisible, async (open) => {
    if (open && props.idExtrapolation) {
        await pvStore.fetchExtrapolationByAnalyseId(props.idExtrapolation)
    }
})

// ================================
// INTERACTIONS
// ================================
function onClickOpenExport(): void {
    exportDialogVisible.value = true
}

function onClickDownloadExcel(): void {
    downloadChartAsExcel(getPlotlyGraph.value, {
        chartSlug: showExtrapolationView.value
            ? "consommation-electricite-extrapolee"
            : "consommation-electricite",
        buildingSlug: props.buildingId,
        xAxisLabel: "Date",
        sheetName: "Consommation",
    })
}

async function onClickSecondaryExtrapolation(): Promise<void> {
    if (props.idExtrapolation) {
        showExtrapolationView.value = !showExtrapolationView.value
        return
    }
    isExtrapolating.value = true
    try {
        await $createAnalyseExtrapolation({
            _id_source: props.sourceId,
            _id_building: props.buildingId,
        })
        toast.add({
            severity: "success",
            summary: t("toastExtrapolationStarted"),
            life: 8000,
        })
        emit("refreshed")
    } catch {
        toast.add({
            severity: "error",
            summary: t("toastExtrapolationError"),
            life: 8000,
        })
    } finally {
        isExtrapolating.value = false
    }
}

function onClickOpenDeleteExtrapolationConfirm(): void {
    if (!props.idExtrapolation || isDeleting.value) return

    confirm.require({
        header: t("confirmDeleteTitle"),
        message: t("confirmDeleteMessage"),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("confirmReject"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("confirmAccept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("confirmReject"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            const id = props.idExtrapolation
            if (!id) return
            isDeleting.value = true
            try {
                await $deleteAnalysePvExtrapolationById(id)
                showExtrapolationView.value = false
                pvStore.unsetExtrapolation()
                toast.add({
                    severity: "success",
                    summary: t("toastDeleteOk"),
                    life: 6000,
                })
                emit("refreshed")
            } catch {
                toast.add({
                    severity: "error",
                    summary: t("toastDeleteError"),
                    life: 8000,
                })
            } finally {
                isDeleting.value = false
            }
        },
        reject: () => {},
    })
}
</script>

<template>
    <BaseTitledCard
        class="pv-consommation-electricite-card shadow-sm border border-surface-200 overflow-hidden"
        content-class="p-4 sm:p-6"
        header-class="!text-left"
    >
        <template #header>
            <div
                class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full font-semibold"
            >
                <span class="text-surface-900">
                    {{ t("title") }}
                </span>
                <div class="flex flex-wrap items-center gap-2">
                    <BaseButton
                        v-if="idExtrapolation && showExtrapolationView"
                        icon="pi pi-trash"
                        severity="danger"
                        text
                        size="small"
                        v-tooltip="t('extrapolationTooltipDelete')"
                        rounded
                        :aria-label="t('extrapolationTooltipDelete')"
                        :loading="isDeleting"
                        @click="onClickOpenDeleteExtrapolationConfirm"
                    />
                    <BaseButton
                        v-if="getShowSecondaryExtrapolation"
                        :label="getExtrapolationSecondaryLabel"
                        severity="secondary"
                        size="small"
                        :loading="isExtrapolating"
                        @click="onClickSecondaryExtrapolation"
                    />

                    <BaseButton
                        :label="t('exportExcel')"
                        icon="pi pi-file-excel"
                        severity="secondary"
                        text
                        size="small"
                        :disabled="!getPlotlyGraph"
                        @click="onClickDownloadExcel"
                    />
                    <BaseButton
                        :label="t('exportData')"
                        icon="pi pi-download"
                        size="small"
                        @click="onClickOpenExport"
                    />
                </div>
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <BaseMessage
                    v-if="nbHourlyDays < 30"
                    severity="warn"
                    :closable="false"
                >
                    {{ getMissingHourlyMessage }}
                </BaseMessage>

                <div
                    v-if="getPlotlyGraph"
                    :key="chartInstanceKey"
                    class="h-[28rem] w-full min-h-[20rem]"
                >
                    <PlotlyChart :graph="getPlotlyGraph" />
                </div>
            </div>
        </template>
    </BaseTitledCard>

    <PvConsommationExportDialog
        v-model:visible="exportDialogVisible"
        :source-id="sourceId"
        :id-extrapolation="idExtrapolation"
        :extrapolation-doc="getExtrapolationDocForExport"
    />
</template>
